import { useEffect, useState } from "react";
import { Sprite } from "./Sprite";
import css from "./primitives.module.css";

type AssetArtProps = {
  /**
   * Every image that could stand in, best first — from `artCandidates()`.
   *
   * Empty is normal and is most tabs: it means draw the sprite.
   */
  coverUrls: string[];
  /** The sprite to draw when there is no cover, or when one fails to load. */
  spriteSrc: string;
  size: number;
  /** Passed through to the sprite; see `Sprite`'s note on decorative art. */
  label?: string;
  /**
   * Draw the cover whole, stretched over the zone, with something filling the
   * space its aspect ratio leaves over.
   *
   * **What fills it is a CSS decision, not this component's** — see ART FILL
   * MODE in `primitives.module.css`. Today it is the game's tint; the blurred
   * blow-up is one commented rule away and everything it needs is still here.
   *
   * Off by default, and deliberately so — it only makes sense where the art
   * zone is wider than the art. In the detail rail and the Studio table the
   * art *is* the zone, so there is nothing to fill.
   */
  fillBackdrop?: boolean;
};

/**
 * A tab's art: its cover if it has one, otherwise the sprite — DE-D1.
 *
 * §5 of the design handoff describes the sprite system as *the* art system:
 * "no bespoke art for a new tab — only a hue". That stays true as the
 * **default**. Its real property is that adding a tab costs nothing and the
 * grid never has a hole in it, and that property survives here: a tab without a
 * cover renders exactly what it rendered before this file existed.
 *
 * ### A failed load advances; it does not give up
 *
 * `game-cover-art` FR-4, GC-D1, and the reason this holds an **index** rather
 * than a boolean.
 *
 * An image that fails to load is not an edge case — an object removed in
 * Studio, a patron on a bad connection, a key that never reached storage. Left
 * alone the browser draws its own broken-image glyph, which is worse than the
 * sprite in every way and appears exactly where the design promised something
 * deliberate.
 *
 * When there was one candidate, routing every failure to the sprite was right.
 * With a chain it is **wrong in a way that tests clean**: a tab whose own cover
 * is broken should show its game's art, and the give-up version behaves
 * perfectly on every tab whose cover loads. It is wrong only for the broken
 * ones, which are exactly the tabs nobody is looking at while checking it.
 *
 * So `onError` steps to the next candidate and the sprite renders only once the
 * list is exhausted.
 *
 * The index also stops the retry loop the boolean used to: `onError` on a
 * re-rendered `<img>` would otherwise fire on every parent render — a broken
 * image in a grid that re-renders as the reader types is a request per
 * keystroke.
 */
export function AssetArt({ coverUrls, spriteSrc, size, label, fillBackdrop = false }: AssetArtProps) {
  const [index, setIndex] = useState(0);

  /*
   * A different asset in the same position is a different set of images, so the
   * previous one's failures must not carry over. Grids reuse components by
   * position, so this is ordinary, not exceptional.
   *
   * Keyed on the joined list rather than the array identity: `artCandidates`
   * returns a fresh array every render, and depending on identity would reset
   * the index on every render and retry a broken image forever.
   */
  const key = coverUrls.join("\u0000");
  useEffect(() => setIndex(0), [key]);

  const current = coverUrls[index];
  if (!current) {
    return <Sprite src={spriteSrc} size={size} label={label} />;
  }

  return (
    <>
      {/*
       * The blurred backdrop — **hidden in CSS today** (ART FILL MODE).
       *
       * Rendered anyway, because that is what keeps the switch to one line of
       * stylesheet. Same `src` as the foreground, so a browser that loads a
       * `display: none` image serves it from the same cache entry: a decode at
       * worst, never a second request.
       *
       * When it is on: `scale` past the edges because a blur samples beyond its
       * own bounds and would otherwise fade to transparent at the rim, showing
       * a hairline of the tint all the way round. Darkened, because the
       * foreground has to stay the thing being looked at.
       */}
      {fillBackdrop && <img className={css.coverBackdrop} src={current} alt="" aria-hidden />}
      {/*
       * A spacer the size the sprite would have been.
       *
       * It is the only thing left in flow, so the art zone keeps exactly the
       * height it has always had and a covered card still lines up with an
       * uncovered one (NFR-2). The art itself is taken out of flow and
       * stretched over the whole zone — **including the zone's padding**,
       * because `inset` resolves against the padding box.
       *
       * Reported by Adam 2026-09-13: "the area is a square, and the image is a
       * square, but the image is not taking up the entire area." It was the
       * 16–18px of padding, which exists so pixel-art sprites have room to
       * breathe on the tint wash. A cover does not want breathing room; it
       * wants the tile.
       */}
      {fillBackdrop && (
        <span className={css.coverSpacer} style={{ width: size, height: size }} aria-hidden />
      )}
      <img
        className={fillBackdrop ? `${css.cover} ${css.coverFill}` : css.cover}
        // Keyed so React swaps the element rather than mutating src on the same
        // one, which would leave the failed image painted until the next loads.
        key={current}
        src={current}
        width={size}
        height={size}
        style={fillBackdrop ? undefined : { width: size, height: size }}
        onError={() => setIndex((i) => i + 1)}
      /*
       * NFR-4. The card, the rail and the table all name the tab in text
       * immediately beside this, so a described image would have a screen
       * reader say the title twice. It is decoration *here* because the
       * information it carries is already present.
       */
        alt=""
        aria-hidden
        // Art is never the reason to hold up the rest of the card (NFR-3).
        loading="lazy"
        decoding="async"
      />
    </>
  );
}
